import React, { useState } from 'react';
import { useUserHook } from '../../../../hooks/admin/user/use-user';
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
} from 'antd';
import {
  FaCheckCircle,
  FaChevronDown,
  FaEdit,
  FaEye,
} from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import { MdBlock } from 'react-icons/md';
import {
  blockCommentUser,
  blockUser,
  confirmCreator,
  deleteUser,
  rejectCreatorAPI,
  toggleChangeReciveDonateAPI,
  unBlockCommentUser,
} from '../../../../api/admin/user.api';
import { promptTotp } from '../../../../utils/admin-stepup';
import toast from 'react-hot-toast';
import { CreateUserModal } from '../../../register/create-form-user';
import CreatorRequestDialog from '../../../user-setting/components/creator-request-dialog';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Ban, Check, CircleDot, Clock, X } from 'lucide-react';
import { AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import { ekycErrorMessage } from '../../../donate-goal/ekyc-flow';
import RejectReasonDialog from '../../components/request-review/reject-reason-dialog';
import EligibilityDialog from '../../components/request-review/eligibility-dialog';
import { useApprovalFlow } from '../../components/request-review/use-approval-flow';
import { successText } from '../../components/request-review/review-rules';

const ManagementRequestCreator = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState<{
    fullName?: string;
    email?: string;
    status?: number;
    userType: 'NORMAL';
    IsRequestCreator: true;
    isBlockComment?: boolean;
    canReceivedDonation?: boolean;
    isVerifyEmail?: boolean;
  }>({
    userType: 'NORMAL',
    IsRequestCreator: true,
  });

  // Cờ tải lại bảng sau mỗi thao tác: phải khai báo trước và truyền vào hook (trước đây bị bỏ sót, 08/10).
  const [isRefetch, setIsRefetch] = useState(false);
  const { data, total, loading } = useUserHook({
    isRefetch,
    pageNumber,
    pageSize,
    filters,
  });

  const { Option } = Select;

  const handleTableChange = (pagination: any) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleFilterChange = (
    key: string,
    value: string | number | undefined | boolean
  ) => {
    setPageNumber(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [isOpenCreatorRequestDialog, setIsOpenCreatorRequestDialog] =
    useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const handleBlock = async (userId: string) => {
    const totp = promptTotp('khoá/mở tài khoản');
    if (!totp) return;
    try {
      await blockUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã cập nhật trạng thái tài khoản'); // QA S-36
    } catch (error) {
      toast.error('Thao tác thất bại');
    }
  };
  const handleUnBlockComment = async (userId: string) => {
    const totp = promptTotp('mở bình luận');
    if (!totp) return;
    try {
      await unBlockCommentUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã mở bình luận cho người dùng'); // QA S-36: trước báo nhầm "Block user"
    } catch (error) {
      toast.error('Mở bình luận thất bại');
    }
  };
  const handleBlockComment = async (userId: string) => {
    const totp = promptTotp('tắt bình luận');
    if (!totp) return;
    try {
      await blockCommentUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã tắt bình luận của người dùng'); // QA S-36: trước báo nhầm "Block user"
    } catch (error) {
      toast.error('Tắt bình luận thất bại');
    }
  };

  // Duyệt tích xanh: xem điều kiện trước. Bắt buộc là đã mở nhận donate; thiếu mục xét duyệt thì phải ghi lý do ngoại lệ.
  const doConfirmCreator = async (userId: string, note?: string) => {
    const totp = promptTotp('duyệt creator');
    if (!totp) return;
    try {
      const res = await confirmCreator(userId, totp, note);
      setIsRefetch((v) => !v);
      toast.success(successText(res, 'Đã duyệt tích xanh creator.'));
    } catch (error) {
      toast.error(ekycErrorMessage(error, 'Duyệt creator thất bại'));
    }
  };
  const approval = useApprovalFlow('creator', doConfirmCreator);

  // Từ chối: chọn lý do có sẵn, "Lý do khác" phải ghi thêm. Máy chủ chỉ cho từ chối yêu cầu đang chờ duyệt.
  const [rejectUserId, setRejectUserId] = useState<string | null>(null);
  const handleRejectCreator = async (reasonCode: string, reasonText: string) => {
    if (!rejectUserId) return;
    try {
      const res = await rejectCreatorAPI(rejectUserId, reasonCode, reasonText);
      setRejectUserId(null);
      setIsRefetch((v) => !v);
      toast.success(successText(res, 'Đã từ chối yêu cầu tích xanh creator.'));
    } catch (error) {
      toast.error(ekycErrorMessage(error, 'Từ chối thất bại'));
      throw error;
    }
  };

  const [eligibilityOf, setEligibilityOf] = useState<{ userId: string; name?: string } | null>(
    null
  );

  const handleDelete = async (userId: string) => {
    const totp = promptTotp('xoá user');
    if (!totp) return;
    try {
      await deleteUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã xoá người dùng');
    } catch (error) {
      toast.error('Xoá người dùng thất bại');
    }
  };

  const handleToggleChangeReciveDonate = async (id: string) => {
    const totp = promptTotp('bật/tắt nhận donate');
    if (!totp) return;
    try {
      await toggleChangeReciveDonateAPI(id, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã cập nhật quyền nhận donate');
    } catch (error: any) {
      // Bật nhận donate cho người thiếu điều kiện bắt buộc thì máy chủ trả 400 kèm danh sách mục thiếu.
      const d = error?.response?.data;
      toast.error(d?.detail || d?.message || 'Cập nhật quyền nhận donate thất bại');
    }
  };

  const navigator = useNavigate();

  const columns = [
    {
      title: 'Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_, record) => {
        return (
          <div
            className="adm-link cursor-pointer"
            onClick={() => {
              navigator(`/user/${record.userName}`);
            }}
          >
            {record.fullName}
          </div>
        );
      },
    },
    {
      title: 'Email / Tên tài khoản',
      dataIndex: 'email',
      key: 'email',
      render: (_, record) => {
        return (
          <div>
            <div>{record.email}</div>
            <div>{record.userName}</div>
          </div>
        );
      },
    },
    {
      title: 'Thời gian đăng kí',
      dataIndex: 'registeredDate',
      key: 'registeredDate',
      render: (_, record) => {
        return <div>{dayjs(record.registeredDate).format('DD/MM/YYYY')}</div>;
      },
    },
    {
      title: 'Xác minh email',
      dataIndex: 'isVerifyEmail',
      key: 'isVerifyEmail',
      sorter: (a, b) => a.isVerifyEmail - b.isVerifyEmail,
      render: (isVerifyEmail) => {
        return (
          <div>
            {isVerifyEmail ? (
              <AdminStatusPill tone="success" icon={<Check size={12} />}>Đã xác minh</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger" icon={<X size={12} />}>Chưa xác minh</AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Yêu cầu Creator',
      dataIndex: 'isRequestCreator',
      key: 'isRequestCreator',
      sorter: (a, b) => a.isRequestCreator - b.isRequestCreator,
      render: (isRequestCreator, record) => {
        return (
          <div>
            {record?.userType === 'NORMAL' && isRequestCreator ? (
              <span
                className="cursor-pointer inline-flex"
                onClick={() => {
                  console.log(record);
                  setSelectedUserId(record.id);
                  setIsOpenCreatorRequestDialog(true);
                }}
              >
                <AdminStatusPill tone="warning" icon={<Clock size={12} />}>
                  Đang chờ duyệt
                </AdminStatusPill>
              </span>
            ) : record?.userType === 'CONTENT_CREATOR' ? (
              <AdminStatusPill tone="success">Đã là creator</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger" icon={<X size={12} />}>Chưa yêu cầu</AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Donate',
      dataIndex: 'canReceiveDonation',
      key: 'canReceiveDonation',
      sorter: (a, b) => a.canReceiveDonation - b.canReceiveDonation,
      render: (canReceiveDonation) => {
        return (
          <div>
            {canReceiveDonation ? (
              <AdminStatusPill tone="success" icon={<Check size={12} />}>Đã kích hoạt</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger" icon={<X size={12} />}>Chưa kích hoạt</AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isBlock',
      key: 'isBlock',
      sorter: (a, b) => a.isBlock - b.isBlock,
      render: (isBlock) => {
        return (
          <div>
            {!isBlock ? (
              <AdminStatusPill tone="success" icon={<CircleDot size={12} />}>Đang hoạt động</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger" icon={<Ban size={12} />}>Đã khoá</AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Comment',
      dataIndex: 'isBlockComment',
      key: 'isBlockComment',
      sorter: (a, b) => a.isBlockComment - b.isBlockComment,
      render: (isBlockComment) => {
        return (
          <div>
            {!isBlockComment ? (
              <AdminStatusPill tone="success" icon={<CircleDot size={12} />}>Đang hoạt động</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger" icon={<Ban size={12} />}>Đã khoá bình luận</AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'operation',
      render: (record) => (
        <Space size="middle">
          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: !record.isBlock ? 'Khoá tài khoản' : 'Mở khoá',
                  icon: <MdBlock className="mr-2" />,
                  danger: true,
                  onClick: () => handleBlock(record.id),
                },
                {
                  key: '2',
                  label:
                    record?.userType === 'NORMAL' && record?.isRequestCreator
                      ? 'Chấp nhận yêu cầu'
                      : 'Chưa có yêu cầu',
                  icon: <FaEdit className="mr-2" />,
                  onClick: () =>
                    record?.userType === 'NORMAL' && record?.isRequestCreator
                      ? approval.start(record.id)
                      : toast.error('Chưa có yêu cầu'),
                  disabled:
                    record?.userType === 'CONTENT_CREATOR' ||
                    !record?.isRequestCreator,
                },
                // Cờ đang xin creator chỉ bật khi yêu cầu đang chờ duyệt (bị từ chối thì máy chủ tắt cờ).
                ...(record?.userType === 'NORMAL' && record?.isRequestCreator
                  ? [
                      {
                        key: 'reject',
                        label: 'Từ chối yêu cầu',
                        icon: <IoCloseOutline className="mr-2" />,
                        danger: true,
                        onClick: () => setRejectUserId(record.id),
                      },
                    ]
                  : []),
                {
                  key: 'eligibility',
                  label: 'Xem điều kiện',
                  icon: <FaEye className="mr-2" />,
                  onClick: () =>
                    setEligibilityOf({ userId: record.id, name: record.fullName }),
                },
                {
                  key: '3',
                  label: 'Xoá user',
                  icon: <FaEye className="mr-2" />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Bạn có chắc chắn muốn xoá người dùng này?',
                      content: 'Hành động này không thể hoàn tác',
                      okText: 'Xoá',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.id),
                    });
                  },
                },
                {
                  key: '4',
                  label: record?.canReceiveDonation
                    ? 'Tắt nhận donate '
                    : 'Bật nhận donate',
                  icon: record?.canReceiveDonation ? (
                    <IoCloseOutline className="mr-2" />
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  ),
                  onClick: () => {
                    Modal.confirm({
                      title: record?.canReceiveDonation
                        ? 'Bạn có chắc chắn muốn tắt nhận donate?'
                        : 'Bạn có chắc chắn muốn bật nhận donate?',
                      content: 'Hành động này có thể ảnh hưởng đến người dùng',
                      okText: 'Xác nhận',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () => handleToggleChangeReciveDonate(record.id),
                    });
                  },
                },
                {
                  key: '5',
                  label: !record?.isBlockComment
                    ? 'Tắt bình luận '
                    : 'Bật bình luận',
                  icon: !record?.isBlockComment ? (
                    <IoCloseOutline className="mr-2" />
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  ),
                  onClick: () => {
                    Modal.confirm({
                      title: !record?.isBlockComment
                        ? 'Bạn có chắc chắn muốn tắt bình luận?'
                        : 'Bạn có chắc chắn muốn bật bình luận?',
                      content: 'Hành động này có thể ảnh hưởng đến người dùng',
                      okText: 'Xác nhận',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () =>
                        !record?.isBlockComment
                          ? handleUnBlockComment(record.id)
                          : handleBlockComment(record.id),
                    });
                  },
                },
              ],
            }}
          >
            <Button>
              <Space>
                Actions
                <FaChevronDown />
              </Space>
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Yêu cầu Creator"
        description="Người dùng đăng ký làm creator, kèm trạng thái xác minh email và donate."
      />
      {/* <CreateUserModal /> */}
      <AdminCard title="Bộ lọc tìm kiếm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="adm-field">
            <span>Tên</span>
            <Input
              className="w-full"
              placeholder="Tìm theo tên"
              value={filters.fullName || ''}
              onChange={(e) => handleFilterChange('fullName', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <span>Email</span>
            <Input
              className="w-full"
              placeholder="Tìm theo email"
              value={filters.email || ''}
              onChange={(e) => handleFilterChange('email', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <span>Xác minh email</span>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.isVerifyEmail}
              onChange={(value) => handleFilterChange('isVerifyEmail', value)}
              className="w-full"
            >
              <Option value>Đã kích hoạt</Option>
              <Option value={false}>Chưa xác minh</Option>
            </Select>
          </div>
          <div className="adm-field">
            <span>Donate</span>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.canReceivedDonation}
              onChange={(value) =>
                handleFilterChange('canReceivedDonation', value)
              }
              className="w-full"
            >
              <Option value>Đang hoạt động</Option>
              <Option value={false}>Chưa kích hoạt</Option>
            </Select>
          </div>
          <div className="adm-field">
            <span>Trạng thái</span>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              className="w-full"
            >
              <Option value={0}>Đang hoạt động</Option>
              <Option value={1}>Chưa hoạt động</Option>
            </Select>
          </div>
          <div className="adm-field">
            <span>Bình luận</span>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.isBlockComment}
              onChange={(value) => handleFilterChange('isBlockComment', value)}
              className="w-full"
            >
              <Option value={false}>Đang hoạt động</Option>
              <Option value>Bị khóa</Option>
            </Select>
          </div>
        </div>
      </AdminCard>
      <AdminCard flush>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={{
            current: pageNumber,
            pageSize,
            total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </AdminCard>
      {approval.dialog}
      <RejectReasonDialog
        open={!!rejectUserId}
        title="Từ chối yêu cầu tích xanh creator"
        onCancel={() => setRejectUserId(null)}
        onSubmit={handleRejectCreator}
      />
      <EligibilityDialog
        kind="creator"
        userId={eligibilityOf?.userId ?? null}
        name={eligibilityOf?.name}
        onClose={() => setEligibilityOf(null)}
      />
      {/* Creator Request Dialog */}
      {selectedUserId && (
        <CreatorRequestDialog
          userId={selectedUserId}
          isOpen={isOpenCreatorRequestDialog}
          onClose={() => {
            setIsOpenCreatorRequestDialog(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
};

export default ManagementRequestCreator;
